import { Box, InputAdornment, Paper, Stack } from '@mui/material'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import { ReactComponent as NextIcon } from 'assets/arrow_close_logo.svg'
import { ReactComponent as CrossCloseLogo } from 'assets/cross_close_logo.svg'
import { ReactComponent as CheckBox } from 'assets/icons/checkBox.svg'
import CheckBoxActive from 'assets/icons/checkBoxActive'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { COLORS } from 'styles/colors'
import Preloader from 'ui/Preloader'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { BottomButton } from './AutocompleteInput/components/BottomButton'
import { ListboxComponent } from './ListBoxComponent'

const filterOptions = createFilterOptions({
	matchFrom: 'start',
	stringify: (option: ArrayType) => option.title,
})

const icon = <CheckBox />
const checkedIcon = <CheckBoxActive />
type ArrayType = {
	id?: number
	title: string
	description?: string
	group?: string
	disabled?: boolean
}
type AutocompletePropsType = {
	availableList?: ArrayType[]
	isMultiple?: boolean
	limitTags?: number
	label: string
	widthForInput: string
	isError?: boolean
	isCheckbox?: boolean
	onClick?: (type) => void
	selectedValues?: number[]
	onClickListButton?: () => void
	textDescriptionForClickButton?: string
	needChip?: boolean
	isDisable?: boolean
	loading?: boolean
	bgInputColor?: string
	startIcon?: ReactElement
	isShowTags?: boolean

	variantSize?: 'small' | 'medium'
	helperText?: string
}

export default function AutocompleteInput(props: AutocompletePropsType) {
	const {
		availableList,
		limitTags,
		isMultiple = true,
		label,
		widthForInput,
		isError,
		isCheckbox,
		onClickListButton,
		textDescriptionForClickButton,
		onClick,
		selectedValues = [],
		needChip,
		isDisable,
		loading,
		bgInputColor = 'transparent',
		startIcon,
		isShowTags = true,
		variantSize = 'small',
		helperText,
	} = props
	const [value, setValue] = useState([])
	const [inputValue, setInputValue] = useState('')

	useEffect(() => {
		if (!availableList) return

		const newValue = availableList.filter(item =>
			selectedValues.includes(item?.id),
		)
		setValue(newValue)
	}, [selectedValues, availableList])

	// Очистка inputValue только при полном сбросе фильтров извне
	useEffect(() => {
		if (selectedValues.length === 0 && value.length === 0) {
			setInputValue('')
		}
	}, [selectedValues.length, value.length])

	const filterValues = useCallback(
		(id: number | undefined) => {
			setValue(prevState => prevState.filter(el => el.id !== id))
			onClick?.(id)
		},
		[onClick],
	)
	const shrinked = (isShowTags && value?.length > 0) || inputValue?.length > 0

	const sx = useMemo(
		() => ({
			width: widthForInput || '100%',
			backgroundColor: bgInputColor,
			borderRadius: '8px',
			'& label.Mui-focused': {
				paddingLeft: !shrinked && startIcon ? '24px' : '0px',
				color: `${COLORS.hoverInputBorder}`,
				fontSize: '14px',
			},
			'.MuiAutocomplete-inputRoot': {
				opacity: !isDisable ? 1 : 0.5,
			},

			'.MuiInputBase-root': {
				minHeight: variantSize === 'medium' ? '54px' : '40px',
				boxSizing: 'border-box',
				paddingRight: '8px',

				paddingLeft: startIcon ? '0px' : shrinked ? '16px' : '8px',
				display: 'flex',
				alignItems: 'center',
			},

			'& .MuiInputBase-input': {
				paddingLeft: 0,
				fontSize: '14px',
			},
			'& .MuiInputLabel-outlined': {
				paddingLeft: !shrinked && startIcon ? '24px' : '0px',
				fontSize: '14px',
			},
			'& .MuiOutlinedInput-root': {
				fontSize: '14px',
				borderRadius: '8px',
				borderColor: isError ? COLORS.deviationFont : COLORS.inputBorder,
				'&.Mui-focused fieldset': {
					border: `1px solid ${isError ? COLORS.error : COLORS.inputBorder}`,
					color: COLORS.main,
					boxShadow: isError ? 'none' : '0px 4px 16px 0px #00000014',
				},
				'&:hover fieldset': {
					opacity: !isDisable ? 1 : 0.5,
					boxShadow:
						!isDisable && !isError ? '0px 4px 16px 0px #00000014' : 'none',
					border: `1px solid ${
						isError
							? COLORS.error
							: !isDisable
								? COLORS.inputBorder
								: COLORS.icon
					}`,
				},
				'&.Mui-error fieldset': {
					border: `1px solid ${COLORS.error}`,
				},
			},
			'& .MuiFormHelperText-root': {
				marginLeft: 0,
				whiteSpace: 'nowrap',
			},
			'&.MuiOutlinedInput-input': {
				borderColor: isError ? COLORS.deviationFont : COLORS.inputBorder,
			},
		}),
		[
			widthForInput,
			bgInputColor,
			isShowTags,
			value?.length,
			inputValue?.length,
			startIcon,
			isDisable,
			isError,
			variantSize,
			shrinked,
		],
	)
	return (
		<Box sx={{ width: widthForInput || '100%' }}>
			<Autocomplete
				sx={sx}
				multiple={isMultiple}
				size={variantSize}
				disabled={isDisable}
				disableClearable
				disablePortal
				// isOptionEqualToValue={(option, value) => option.id === value.id}
				isOptionEqualToValue={(option, value) => {
					const val = Array.isArray(value) ? value[0] : value
					return option.id === val?.id
				}}
				fullWidth
				loading={loading}
				loadingText={
					<Box sx={{ position: 'absolute', top: '40%', left: '50%' }}>
						<Preloader />
					</Box>
				}
				limitTags={limitTags}
				getLimitTagsText={more => `...`}
				noOptionsText={
					<Stack direction='column' justifyContent='space-between'>
						<TypographySecondary>Результат поиска: 0</TypographySecondary>
						<BottomButton
							onClickListButton={onClickListButton}
							textDescriptionForClickButton={textDescriptionForClickButton}
						/>
					</Stack>
				}
				value={value}
				getOptionLabel={() => ''}
				renderTags={
					isShowTags
						? values =>
								values.map((value, index) => (
									<TypographyPrimary
										key={index}
										sx={{
											maxWidth: '150px',
											display: 'inline-block',
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
										}}
									>
										{value.title}
										{values.length > 1 && index < values.length - 1 ? ',' : ''}
									</TypographyPrimary>
								))
						: () => null
				}
				filterOptions={filterOptions}
				onChange={useCallback((event, newValue) => {
					setValue(newValue)
				}, [])}
				getOptionDisabled={option => option.disabled}
				ListboxComponent={useCallback(
					listboxProps => (
						<ListboxComponent
							{...listboxProps}
							isGrouped={availableList?.some(el => el.group)}
							onClickListButton={onClickListButton}
							textDescriptionForClickButton={textDescriptionForClickButton}
						/>
					),
					[availableList, onClickListButton, textDescriptionForClickButton],
				)}
				inputValue={inputValue}
				PaperComponent={useCallback(
					props => (
						<Box
							sx={{
								marginTop: '4px',
								borderRadius: '8px',
								border: `1px solid ${COLORS.inputBorder}`,
								boxShadow: 'none',
								'& .MuiAutocomplete-noOptions': {
									fontSize: '12px',
									height: '200px',
								},
							}}
						>
							<Paper
								{...props}
								style={{
									borderRadius: '8px',
									boxShadow: '0px 4px 16px 0px #00000014',
									height: '200px',
								}}
							/>
						</Box>
					),
					[],
				)}
				onInputChange={useCallback((event, newInputValue, reason) => {
					// Сохраняем введенный текст только если это ввод пользователя,
					// но НЕ очищаем при выборе опции
					if (reason === 'input') {
						setInputValue(newInputValue)
					} else if (reason === 'clear') {
						setInputValue('')
					}
					// При 'selectOption' не изменяем inputValue, оставляем текущий
				}, [])}
				options={useMemo(
					() =>
						availableList?.map(option => ({
							group: option?.group,
							...option,
						})) || [],
					[availableList],
				)}
				groupBy={option => option?.group}
				disableCloseOnSelect={true}
				renderOption={useCallback(
					(props, option, { selected }) => {
						return (
							<li
								{...props}
								key={option.id ? option.id : option.title}
								onClick={() => onClick && onClick(option.id)}
								style={{
									width: '100%',
									padding: '4px 16px',
									display: 'flex',
									alignItems: 'flex-start',
									gap: '8px',
									backgroundColor: selected
										? COLORS.mainSecondaryOpacity
										: 'transparent',
								}}
								onMouseEnter={e =>
									(e.currentTarget.style.backgroundColor =
										COLORS.mainSecondaryOpacity)
								}
								onMouseLeave={e =>
									(e.currentTarget.style.backgroundColor = selected
										? COLORS.mainSecondaryOpacity
										: 'transparent')
								}
							>
								{isCheckbox && (
									<Checkbox
										icon={icon}
										disableRipple
										checkedIcon={checkedIcon}
										style={{ padding: '0px 10px 0px 0px', margin: '0px' }}
										checked={selected}
									/>
								)}

								<Box sx={{ display: 'flex', flexDirection: 'column' }}>
									<TypographyPrimary>{option.title}</TypographyPrimary>
									{option.description && (
										<TypographySecondary
											sx={{ fontSize: '12px', color: COLORS.secondary }}
										>
											{option.description}
										</TypographySecondary>
									)}
								</Box>
							</li>
						)
					},
					[onClick, isCheckbox],
				)}
				renderGroup={useCallback(params => {
					if (params.group) {
						return (
							<Box key={params.key} width={'100%'}>
								<Box
									sx={{
										position: 'sticky',
										top: '-8px',
										padding: '8px 16px',
										backgroundColor: '#fff',
										zIndex: 2,
									}}
								>
									<TypographySecondary>{params.group}</TypographySecondary>
								</Box>
								{params.children}
							</Box>
						)
					} else {
						return <Box key={params.key}>{params.children}</Box>
					}
				}, [])}
				popupIcon={<NextIcon stroke={isError ? COLORS.error : COLORS.icon} />}
				renderInput={params => (
					<TextField
						error={isError}
						sx={sx}
						{...params}
						label={label}
						helperText={isError ? helperText : undefined}
						InputLabelProps={{
							...params.InputLabelProps,
							shrink:
								(isShowTags && value?.length > 0) || inputValue?.length > 0,
						}}
						InputProps={{
							...params.InputProps,

							startAdornment: startIcon ? (
								<>
									<InputAdornment
										position='start'
										sx={{ padding: '0px', margin: '0px' }}
									>
										{startIcon}
									</InputAdornment>
									{params.InputProps.startAdornment}
								</>
							) : (
								params.InputProps.startAdornment
							),
						}}
					/>
				)}
			/>
			{needChip && value.length > 0 && (
				<Box
					display={'flex'}
					flexDirection={'row'}
					gap={'4px'}
					flexWrap={'wrap'}
					mt={'16px'}
					sx={{
						maxWidth: '100%',
						minWidth: 0,
						maxHeight: '260px',
						overflowX: 'hidden',
						overflowY: 'auto',
					}}
				>
					{value.map(el => (
						<Box
							key={el.id}
							sx={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'flex-start',
								gap: '6px',
								padding: '8px 16px',
								backgroundColor: COLORS.background,
								borderRadius: '12px',
								maxWidth: '100%',
								minWidth: 0,
								'&:hover': {
									backgroundColor: COLORS.mainSecondaryOpacity,
									'& > svg': {
										stroke: COLORS.main + '!important',
									},
								},
							}}
						>
							<TypographyPrimary
								sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
							>
								{el.title}
							</TypographyPrimary>
							<div>
								<CrossCloseLogo
									style={{
										cursor: 'pointer',
										width: '16px',
										height: '16px',
										stroke: COLORS.secondary,
									}}
									onClick={() => filterValues(el.id)}
								/>
							</div>
						</Box>
					))}
				</Box>
			)}
		</Box>
	)
}
